import { I18n } from "@assets/strings";
import React, { JSX, useEffect } from "react";
import { useHistory } from "@core/routing/useHistory";
import styled from "styled-components";
import { Row, row, Spacer, stack } from "@ui/components/layout";
import { Toolbar } from "@ui/components/toolbar";
import { desktop, gap, mobile } from "@ui/styles/utils";

import { registerLocale } from "react-datepicker";

import copy from "copy-to-clipboard";
import { fr } from "date-fns/locale/fr";
import moment from "moment";
import "moment/locale/fr";
import "moment/locale/fr-ca";
import "react-datepicker/dist/react-datepicker.css";
import { ClipLoader } from "react-spinners";
import defaultLiveCourseHeader from "@assets/images/default_live_course_header.jpg";
import cameraRougeImage from "@assets/images/live/camerarouge.png";
import scheduledLiveLogoImage from "@assets/images/live/scheduled_live_logo.png";
import visioYellowImage from "@assets/images/live/visio_yellow.svg";
import { Path } from "@core/routing/path";
import { getLivecourseInfos, useParticipants } from "@domain/livecourse/hooks";
import { LivecourseType } from "@domain/livecourse/livecourse";
import { ImageVersions } from "@domain/media/dtos";
import { useOrganization } from "@domain/organization/hooks";
import { useUserProfile } from "@domain/user/hooks";
import { Person } from "@domain/user/user";
import { useIsMobile } from "@utils/hooks";
import { BackgroundImage } from "@ui/components/backgroundImage";
import { DefaultButton } from "@ui/components/buttons/defaultButton";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { CalendarIconBis, CopyIcon, TimeIcon } from "@ui/components/icons";
import { Page } from "@ui/components/page";
import { LiveMembers } from "@ui/components/profile/community/liveMembers";
import { UserView } from "@ui/components/profile/userView";
import { colors } from "@ui/styles/colors";
import { theme } from "@ui/styles/theme";
import { createToast } from "@ui/toasts/utils";

registerLocale("fr", fr);

moment.locale("fr");

interface LiveOrganizationcoursesInfosProps extends React.HTMLAttributes<HTMLDivElement> {
	livecourseId: string;
	organizationId: string;
}
export const LiveOrganizationcoursesInfos: React.FC<LiveOrganizationcoursesInfosProps> = ({
	livecourseId,
	organizationId,
	...htmlProps
}) => {
	const { result: live, loading } = getLivecourseInfos(livecourseId);
	const history = useHistory();
	moment.locale("fr");
	const { result: organization, loading: loadingOrg } = useOrganization(organizationId);
	const { result: members, loading: loadingMembers, totalElements } = useParticipants(livecourseId);
	const type = live?.type;
	const starthour = moment(live?.startDate).format(" hh:mma ");
	const endhour = moment(live?.endDate).format(" hh:mma ");
	const { result: profile, loading: loadingProf } = useUserProfile(`${live?.organizer}`);
	const isMobile = useIsMobile();
	useEffect(() => {}, []);

	const renderDateBloc = (scheduled: boolean): JSX.Element => (
		<>
			{!scheduled ? <RedVerticalBox /> : <BlueVerticalBox />}

			<StyledDiv>
				{!scheduled ? (
					<DateElementTitle>
						{I18n.t("live.startedat")} {starthour}
					</DateElementTitle>
				) : (
					<DateElementTitle>
						<Row>
							<Bloc style={{ marginTop: 0, marginLeft: 8 }}>
								<TimeIcon color={"darkgray"} />
							</Bloc>
							<Bloc style={{ marginTop: 3, marginLeft: 8 }}>
								{starthour}
								{"  "} à{"  "} {endhour}
							</Bloc>
							<Bloc style={{ marginTop: 0, marginLeft: 33 }}>
								<CalendarIconBis width={20} height={20} />
							</Bloc>
							<Bloc style={{ marginTop: 2, marginLeft: 4 }}>
								<StyledDiv>{moment(live?.startDate).format("DD/MM/YY")}</StyledDiv>
							</Bloc>
						</Row>
					</DateElementTitle>
				)}
			</StyledDiv>
		</>
	);

	const renderRows = (scheduled: boolean): JSX.Element => (
		<FormBloc>
			<Row>
				<StyledImage
					style={{ marginTop: isMobile ? 20 : undefined, width: isMobile ? "100%" : undefined }}
					image={live?.banner}
					defaultUrl={!live?.banner ? defaultLiveCourseHeader : undefined}
					size={ImageVersions.MEDIUM}
				/>
			</Row>
			<Row style={{ marginBottom: 15 }}>
				<img src={cameraRougeImage} width={41} height={25} />
				<PrimaryTitle> {live?.title}</PrimaryTitle>
			</Row>
			<Row
				style={{
					marginBottom: 15,
					background: "#F3F4F6",
					border: "1px solid #D2E1FC",
					boxSizing: "border-box",
					borderRadius: 7,
					width: "fit-content",
					paddingRight: "10%",
				}}
			>
				{renderDateBloc(scheduled)}
			</Row>
			<Row>
				<SecondaryTitle style={{ display: "contents", overflow: "auto" }}> {live?.description}</SecondaryTitle>
			</Row>
			<Row style={{ marginBottom: 15, marginTop: 15 }}>Page</Row>
			<Row style={{ marginBottom: 15 }}>
				<UserView user={organization as Person} />
			</Row>
			<Row style={{ marginBottom: 15 }}>{I18n.t("live.createdby")}</Row>
			<Row style={{ marginBottom: 15 }}>
				<UserView user={profile as Person} />
			</Row>
			<Row style={{ marginBottom: 15 }}>
				{I18n.t("live.participant")} ({totalElements})
			</Row>
			<Row style={{ marginBottom: 15 }}>
				<LiveMembers imageSize={"default"} sample={members} count={members.length} spacing={-8} />
			</Row>
		</FormBloc>
	);

	return loadingOrg || loadingMembers || loading || !live || loadingProf ? (
		<ClipLoader />
	) : (
		<MainContainer containerStyle={{ paddingTop: 0 }} {...htmlProps}>
			<ContentBis>
				<Card>
					{!isMobile && (
						<Row style={{ marginBottom: 0 }}>
							{type === LivecourseType.INSTANT && (
								<Column>
									<LeftPanInstant>
										<img src={visioYellowImage} width={145} height={145} />
										<WhiteRectangle />
										<TextDescription>{I18n.t("live.presentation")}</TextDescription>
									</LeftPanInstant>
								</Column>
							)}
							{type === LivecourseType.SCHEDULED && (
								<Column>
									<LeftPanScheduled>
										<img src={scheduledLiveLogoImage} width={145} height={"auto"} />
										<WhiteRectangle style={{ marginTop: 75 }} />
										<TextDescription>{I18n.t("live.presentation")}</TextDescription>
									</LeftPanScheduled>
								</Column>
							)}
							{type === LivecourseType.INSTANT && renderRows(false)}
							{type === LivecourseType.SCHEDULED && renderRows(true)}
						</Row>
					)}
					{isMobile && (
						<>
							{type === LivecourseType.INSTANT && (
								<Column>
									<LeftPanInstant>
										<img src={visioYellowImage} width={70} height={70} />
										<TextDescription>{I18n.t("live.presentation")}</TextDescription>
									</LeftPanInstant>
								</Column>
							)}
							{type === LivecourseType.SCHEDULED && (
								<Column>
									<LeftPanScheduled>
										<img style={{ top: 56 }} src={scheduledLiveLogoImage} width={70} height={"auto"} />
										<TextDescription style={{ top: 60 }}>{I18n.t("live.presentation")}</TextDescription>
									</LeftPanScheduled>
								</Column>
							)}

							{type === LivecourseType.INSTANT && renderRows(false)}
							{type === LivecourseType.SCHEDULED && renderRows(true)}
						</>
					)}
				</Card>
			</ContentBis>

			{type === LivecourseType.SCHEDULED ? (
				<StyledToolbar>
					<Bottomnav>
						<CopyButton
							color={theme.colors.white}
							onClick={() => {
								try {
									copy(live.liveShareLink);
									createToast(I18n.t("success"), I18n.t("live.copysuccess"), "success");
								} catch (e) {
									//console.error(e);
									createToast(I18n.t("error"), I18n.t("password.copyfailed"), "error");
									throw e;
								}
							}}
						>
							<CopyIcon style={{ marginRight: 13 }} />
							{I18n.t("live.copylink")}
						</CopyButton>
						<Spacer />
						<CustomButton onClick={() => history.push(Path.Organization({ id: organizationId }))}>
							{I18n.t("close")}
						</CustomButton>
					</Bottomnav>
				</StyledToolbar>
			) : (
				<StyledToolbar>
					<Bottomnav>
						<Spacer />
						<PrimaryButton
							onClick={() => {
								if (global.window) {
									global.window.location.href = live.participationLink;
								}
							}}
						>
							{I18n.t("live.start")}
						</PrimaryButton>
					</Bottomnav>
				</StyledToolbar>
			)}
		</MainContainer>
	);
};

const CustomButton = styled(DefaultButton)`
	background-color: ${({ theme }) => theme.colors.secondary};
	color: ${({ theme }) => theme.colors.white};
	svg {
		margin-right: 4px;
		path {
			fill: ${({ theme }) => theme.colors.secondary};
		}
	}

	transition: background-color 0.2s linear;
	&:hover  {
		background-color: ${({ theme }) => theme.colors.secondaryLightTouch};
	}
`;

const StyledDiv = styled.div``;

const CopyButton = styled(DefaultButton)`
	background-color: ${({ theme }) => theme.colors.secondaryLight};
	color: ${({ theme }) => theme.colors.black};
	svg {
		margin-right: 4px;
		path {
			fill: ${({ theme }) => theme.colors.secondary};
		}
	}
	font-weight: 200;
	transition: background-color 0.2s linear;
	&:hover  {
		background-color: ${({ theme }) => theme.colors.secondaryLightTouch};
	}
`;

const SecondaryTitle = styled.div`
	color: ${colors.black};
	text-align: left;
	height: 85px;
	max-height: 85px;
	font-family: DM Sans;
	font-style: normal;
	font-weight: normal;
	font-size: 14px;
	line-height: 20px;
	color: #13131a;
	overflow-wrap: anywhere;
`;

const DateElementTitle = styled.div`
	position: relative;
	font-size: 12px;
	left: 10px;
	font-family: Sora;
	font-style: normal;
	font-weight: 600;
	font-size: 12px;
	line-height: 15px;
	color: #000000;
	margin-top: 12px;
`;

const RedVerticalBox = styled.div`
	position: relative;
	left: 3;
	top: 0;
	height: 37px;
	width: 10px;
	background: #e83636;
	border: 1px solid #f5f3f3;
	box-sizing: border-box;
	border-radius: 87px;
`;

const BlueVerticalBox = styled.div`
	position: relative;
	left: 3;
	top: 0;
	height: 37px;
	width: 10px;
	background: ${({ theme }) => theme.colors.secondary};
	border: 1px solid #f5f3f3;
	box-sizing: border-box;
	border-radius: 87px;
`;

const PrimaryTitle = styled.div`
	color: ${colors.black};
	font-weight: 700;
	font-size: 1.2375rem;
	margin-left: 20px;
`;

const Bloc = styled.div`
	position: relative;
	font-family: Sora;
	margin-top: 12px;
	${({ theme }) => theme.colors.primary};
	font-size: 13px;
	font-weight: 100;
`;

const LeftPanScheduled = styled.div`
	@media ${desktop} {
		min-height: 680px;
		width: 120%;
		height: 100%;
		background-color: ${({ theme }) => theme.colors.secondary};
		border-radius: 21px 0px 0px 21px;
		box-shadow: 0 4px 46px 0 rgba(0, 0, 0, 0.1);
		box-shadow: 0 4px 46px 0 rgb(0 0 0 / 10%);
		> img {
			position: relative;
			margin-top: 98px;
			left: 30px;
		}
	}

	@media ${mobile} {
		height: 157px;
		background-color: ${({ theme }) => theme.colors.secondary};
		border-radius: 7px 7px 7px 7px;
		box-shadow: 0 4px 46px 0 rgba(0, 0, 0, 0.1);
		> img {
			position: absolute;
			top: 43px;
			left: 31px;
		}
	}
`;

const LeftPanInstant = styled.div`
	@media ${desktop} {
		min-height: 680px;
		width: 120%;
		height: 100%;
		background-color: ${({ theme }) => theme.colors.primary};
		border-radius: 21px 0px 0px 21px;
		box-shadow: 0 4px 46px 0 rgba(0, 0, 0, 0.1);
		background-color: #e83636;
		box-shadow: 0 4px 46px 0 rgb(0 0 0 / 10%);
		> img {
			position: relative;
			margin-top: 71px;
			left: 30px;
		}
	}

	@media ${mobile} {
		height: 157px;
		background-color: ${({ theme }) => theme.colors.primary};
		box-shadow: 0 4px 46px 0 rgba(0, 0, 0, 0.1);
		border-radius: 7px 7px 7px 7px;
		> img {
			position: absolute;
			top: 43px;
			left: 31px;
		}
	}
`;

const WhiteRectangle = styled.div`
	@media ${desktop} {
		position: relative;
		width: 108px;
		height: 6px;
		left: 35px;
		border-radius: 15px;
		background: ${({ theme }) => theme.colors.white};
		max-width: 234px;
		margin-top: 56px;
	}
`;

const TextDescription = styled.div`
	@media ${desktop} {
		position: relative;
		flex-direction: column;
		left: 35px;
		font-size: 16px;
		color: #ffffff;
		max-width: 234px;
		font-weight: bold;
		text-align: left;
		margin-top: 63px;
		padding-right: 7px;
		font-size: 18px;
		font-family: Sora;
		font-style: normal;
		line-height: 30px;
	}

	@media ${mobile} {
		font-family: Sora;
		font-style: normal;
		font-weight: bold;
		font-size: 16px;
		line-height: 20px;
		color: #ffffff;
		position: absolute;
		top: 58px;
		left: 126px;
		max-width: 211px;
		text-align: left;
	}
`;

const Bottomnav = styled.div`
	@media ${desktop} {
		height: 68px;
		${row(12, "flex-end", "center")};
	}
	@media ${mobile} {
		${stack(10, "center", "center", "reverse")};
	}
`;

const StyledToolbar = styled(Toolbar)`
	> * {
		flex: 1;
		max-width: 630px;
	}
`;

const ContentBis = styled(RoundedBlock)`
	padding: 40px;
	@media ${desktop} {
		padding: 0;
	}
	@media ${mobile} {
		padding: 0;
	}

	background-color: inherit;
	border: none;
	width: 100%;
	${gap(40)};
`;

export const Card = styled(RoundedBlock)`
	margin-bottom: 10%;
	@media ${desktop} {
		${gap(80)};
		margin-left: 8%;
		border-radius: 21px;
	}

	@media ${mobile} {
		border-radius: 7px;
		background-color: #f3f4f6;
	}
`;

const MainContainer = styled(Page)<{ maxWidth?: number }>`
	${stack(40, "center", "center", undefined, true)};
	@media ${desktop} {
		padding-top: 30px;
	}
`;

const Column = styled(Bloc)`
	margin-top: unset;
	border-radius: 21px 0px 0px 21px;
`;

const FormBloc = styled(Bloc)`
	@media ${desktop} {
		padding: 10px;
		padding-left: 18%;
		width: 70%;
	}

	@media ${mobile} {
		padding: 20px;
		padding-top: 0px;
		margin-top: 11px;
		border-radius: 7px;
		background-color: white;
		padding-left: 10%;
	}
`;

const StyledImage = styled(BackgroundImage)`
	border: 1px #17667869 solid;
	margin-bottom: 29px;
	border-radius: 5px;
	width: 100%;
	height: 100%;
	min-height: 149px;
	min-width: 100px;
`;
